// The Holographic Data Navigator: every atom is a glowing node floating in a
// dark HUD, clustered by type, wired by shared-entity links. A live force
// simulation settles the field on load and reheats when you grab a node. You can
// pan (drag the field), zoom (scroll), drag nodes, click to pin one for
// inspection, and spotlight a single type. Visualization only — nothing here
// edits an atom.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Boxes, Crosshair, Minus, Plus, RotateCcw, Waypoints } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { AtomType, NewsAtom } from '@/lib/types';

// World-space canvas. The <svg> viewBox is fixed to these units; a pan/zoom
// transform on the inner <g> is what actually moves the camera.
const W = 960;
const H = 620;
const CENTER = { x: W / 2, y: H / 2 };
const MAX_NODES = 500; // guardrail so the O(n^2) sim stays smooth
const MAX_EDGES = 700;

// Physics.
const ANCHOR_R = 215; // radius of the ring of per-type cluster anchors
const REST = 62; // shared-entity spring rest length
const ALPHA_MIN = 0.02; // sim sleeps below this
const ALPHA_DECAY = 0.985;
const VEL_CLAMP = 16;
const K_MIN = 0.4;
const K_MAX = 4;

// Neon hues tuned to glow on the dark field (SVG needs real values).
const TYPE_COLOR: Record<string, string> = {
  event: '#a78bfa',
  fact: '#38bdf8',
  quote: '#fbbf24',
  entity: '#34d399',
  statistic: '#818cf8',
  media: '#f472b6',
  context: '#94a3b8',
};
const TYPE_ORDER: AtomType[] = ['event', 'fact', 'quote', 'entity', 'statistic', 'media', 'context'];
const colorFor = (t: string) => TYPE_COLOR[t] ?? '#94a3b8';

// Short data label for a node: prefer a named entity, else the head of the
// statement. Truncated so labels stay scannable in the field.
function labelFor(a: NewsAtom): string {
  const ent = a.entities?.[0]?.name?.trim();
  const base = ent && a.atom_type === 'entity' ? ent : (a.statement ?? ent ?? '').trim();
  const clean = base.replace(/\s+/g, ' ');
  return clean.length > 34 ? `${clean.slice(0, 33)}…` : clean;
}
// Below this zoom every node shows its label; above it, only active/neighbors do.
const LABEL_ALL_ZOOM = 1.5;

const HUD_BTN =
  'flex h-7 w-7 items-center justify-center rounded-md border border-slate-700 bg-slate-900/70 ' +
  'text-slate-300 backdrop-blur hover:border-cyan-500/60 hover:text-cyan-300';

// Deterministic PRNG — stable initial scatter, no jitter across rebuilds.
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface SimNode {
  id: string;
  atom: NewsAtom;
  type: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx: number | null; // fixed position while being dragged
  fy: number | null;
  r: number;
  conf: number;
}
interface SimEdge {
  a: number;
  b: number;
}
interface Sim {
  nodes: SimNode[];
  edges: SimEdge[];
  anchors: Map<string, { x: number; y: number }>;
}

// Build the node/edge graph + per-type anchors from an atom set (positions only;
// the force loop mutates them in place afterward).
function buildSim(atoms: NewsAtom[]): { sim: Sim; capped: number } {
  const capped = Math.max(0, atoms.length - MAX_NODES);
  const used = atoms.slice(0, MAX_NODES);
  const rnd = mulberry32(used.length * 2654435761 + 7);

  const types = TYPE_ORDER.filter((t) => used.some((a) => a.atom_type === t));
  const anchors = new Map<string, { x: number; y: number }>();
  types.forEach((t, i) => {
    const ang = (i / Math.max(1, types.length)) * Math.PI * 2 - Math.PI / 2;
    anchors.set(t, { x: CENTER.x + Math.cos(ang) * ANCHOR_R, y: CENTER.y + Math.sin(ang) * ANCHOR_R });
  });

  const nodes: SimNode[] = used.map((a) => {
    const anc = anchors.get(a.atom_type) ?? CENTER;
    const conf = typeof a.review_process?.confidence === 'number' ? a.review_process.confidence : 0.8;
    return {
      id: a.atom_id,
      atom: a,
      type: a.atom_type,
      x: anc.x + (rnd() - 0.5) * 120,
      y: anc.y + (rnd() - 0.5) * 120,
      vx: 0,
      vy: 0,
      fx: null,
      fy: null,
      r: 5 + Math.min(5, Math.max(0, (a.media_anchor?.length ?? 1) - 1)) * 1.6,
      conf: Math.min(1, Math.max(0, conf)),
    };
  });

  // Edges: atoms sharing an entity (by lowercased name), chained per entity to
  // keep the field legible, deduped, capped.
  const byEntity = new Map<string, number[]>();
  used.forEach((a, i) => {
    for (const e of a.entities ?? []) {
      const key = (e.name ?? '').trim().toLowerCase();
      if (!key) continue;
      const arr = byEntity.get(key) ?? [];
      arr.push(i);
      byEntity.set(key, arr);
    }
  });
  const seen = new Set<string>();
  const edges: SimEdge[] = [];
  for (const members of byEntity.values()) {
    if (members.length < 2) continue;
    for (let k = 1; k < members.length && edges.length < MAX_EDGES; k++) {
      const a = members[k - 1];
      const b = members[k];
      if (a === b) continue;
      const ek = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (seen.has(ek)) continue;
      seen.add(ek);
      edges.push({ a, b });
    }
  }

  return { sim: { nodes, edges, anchors }, capped };
}

// One physics step, mutating node positions in place. Scaled by `alpha` so the
// field cools to rest. Repulsion + shared-entity springs + per-type anchor pull.
function stepSim(sim: Sim, alpha: number) {
  const { nodes, edges, anchors } = sim;
  for (const n of nodes) {
    n.vx = 0;
    n.vy = 0;
  }
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      let dx = nodes[i].x - nodes[j].x;
      let dy = nodes[i].y - nodes[j].y;
      let d2 = dx * dx + dy * dy;
      if (d2 < 1) {
        d2 = 1;
        dx = 0.5;
        dy = 0.5;
      }
      const f = Math.min(34, 1100 / d2);
      const d = Math.sqrt(d2);
      nodes[i].vx += (dx / d) * f;
      nodes[i].vy += (dy / d) * f;
      nodes[j].vx -= (dx / d) * f;
      nodes[j].vy -= (dy / d) * f;
    }
  }
  for (const e of edges) {
    const dx = nodes[e.b].x - nodes[e.a].x;
    const dy = nodes[e.b].y - nodes[e.a].y;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const f = (d - REST) * 0.03;
    nodes[e.a].vx += (dx / d) * f;
    nodes[e.a].vy += (dy / d) * f;
    nodes[e.b].vx -= (dx / d) * f;
    nodes[e.b].vy -= (dy / d) * f;
  }
  for (const n of nodes) {
    const anc = anchors.get(n.type) ?? CENTER;
    n.vx += (anc.x - n.x) * 0.02 + (CENTER.x - n.x) * 0.004;
    n.vy += (anc.y - n.y) * 0.02 + (CENTER.y - n.y) * 0.004;
  }
  for (const n of nodes) {
    if (n.fx != null && n.fy != null) {
      n.x = n.fx;
      n.y = n.fy;
      continue;
    }
    n.x += Math.max(-VEL_CLAMP, Math.min(VEL_CLAMP, n.vx)) * alpha;
    n.y += Math.max(-VEL_CLAMP, Math.min(VEL_CLAMP, n.vy)) * alpha;
  }
}

export function AtomCloud({ atoms }: { atoms: NewsAtom[] }) {
  const svgRef = useRef<SVGSVGElement | null>(null);

  // The mutable simulation lives in a ref; a frame counter drives re-render only
  // while the field is hot (or being interacted with).
  const simRef = useRef<Sim | null>(null);
  const [, setFrame] = useState(0);
  const rafRef = useRef(0);
  const runningRef = useRef(false);
  const alphaRef = useRef(1);

  const [capped, setCapped] = useState(0);
  const [view, setView] = useState({ k: 1, tx: 0, ty: 0 });
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [spotlight, setSpotlight] = useState<string | null>(null);

  // Interaction bookkeeping (refs so handlers stay stable, no re-render churn).
  const dragNodeRef = useRef<{ id: string; moved: boolean } | null>(null);
  const panRef = useRef<{ vx: number; vy: number; tx: number; ty: number } | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  function ensureRunning() {
    if (runningRef.current) return;
    runningRef.current = true;
    rafRef.current = requestAnimationFrame(tick);
  }
  function reheat(a = 0.55) {
    alphaRef.current = Math.max(alphaRef.current, a);
    ensureRunning();
  }
  function tick() {
    const sim = simRef.current;
    if (!sim) {
      runningRef.current = false;
      return;
    }
    stepSim(sim, alphaRef.current);
    alphaRef.current *= ALPHA_DECAY;
    setFrame((f) => (f + 1) & 0xffff);
    if (alphaRef.current > ALPHA_MIN || dragNodeRef.current) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      runningRef.current = false;
    }
  }

  // Rebuild + settle whenever the atom set changes.
  useEffect(() => {
    const { sim, capped: c } = buildSim(atoms);
    simRef.current = sim;
    setCapped(c);
    setPinned((p) => (p && sim.nodes.some((n) => n.id === p) ? p : null));
    alphaRef.current = 1;
    ensureRunning();
    return () => {
      cancelAnimationFrame(rafRef.current);
      runningRef.current = false;
    };
  }, [atoms]);

  const sim = simRef.current;
  const nodes = sim?.nodes ?? [];
  const edges = sim?.edges ?? [];

  // ---- camera / coordinate helpers ----
  function toVB(clientX: number, clientY: number) {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }
  function toWorld(clientX: number, clientY: number) {
    const vb = toVB(clientX, clientY);
    const v = viewRef.current;
    return { x: (vb.x - v.tx) / v.k, y: (vb.y - v.ty) / v.k };
  }
  function zoomBy(factor: number, clientX?: number, clientY?: number) {
    setView((v) => {
      const vb = clientX != null && clientY != null ? toVB(clientX, clientY) : { x: W / 2, y: H / 2 };
      const wx = (vb.x - v.tx) / v.k;
      const wy = (vb.y - v.ty) / v.k;
      const k = Math.max(K_MIN, Math.min(K_MAX, v.k * factor));
      return { k, tx: vb.x - wx * k, ty: vb.y - wy * k };
    });
  }

  // ---- pointer interaction ----
  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
  }
  function onNodePointerDown(e: React.PointerEvent, id: string) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragNodeRef.current = { id, moved: false };
    const w = toWorld(e.clientX, e.clientY);
    const n = simRef.current?.nodes.find((nn) => nn.id === id);
    if (n) {
      n.fx = w.x;
      n.fy = w.y;
    }
    reheat(0.5);
  }
  function onBgPointerDown(e: React.PointerEvent) {
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const vb = toVB(e.clientX, e.clientY);
    panRef.current = { vx: vb.x, vy: vb.y, tx: viewRef.current.tx, ty: viewRef.current.ty };
  }
  function onPointerMove(e: React.PointerEvent) {
    const drag = dragNodeRef.current;
    if (drag) {
      drag.moved = true;
      const w = toWorld(e.clientX, e.clientY);
      const n = simRef.current?.nodes.find((nn) => nn.id === drag.id);
      if (n) {
        n.fx = w.x;
        n.fy = w.y;
      }
      reheat(0.5);
      return;
    }
    const pan = panRef.current;
    if (pan) {
      const vb = toVB(e.clientX, e.clientY);
      setView((v) => ({ ...v, tx: pan.tx + (vb.x - pan.vx), ty: pan.ty + (vb.y - pan.vy) }));
    }
  }
  function onPointerUp() {
    const drag = dragNodeRef.current;
    if (drag) {
      const n = simRef.current?.nodes.find((nn) => nn.id === drag.id);
      if (n) {
        n.fx = null;
        n.fy = null;
      }
      if (!drag.moved) setPinned((p) => (p === drag.id ? null : drag.id)); // tap = pin/unpin
      dragNodeRef.current = null;
      reheat(0.35);
    }
    panRef.current = null;
  }
  function resetView() {
    setView({ k: 1, tx: 0, ty: 0 });
  }
  function replay() {
    alphaRef.current = 1;
    ensureRunning();
  }

  // ---- derived: counts, neighbors, cluster label anchors ----
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of atoms) m.set(a.atom_type, (m.get(a.atom_type) ?? 0) + 1);
    return TYPE_ORDER.filter((t) => m.has(t)).map((t) => ({ type: t, n: m.get(t) ?? 0 }));
  }, [atoms]);
  const total = atoms.length;
  const maxCount = Math.max(1, ...counts.map((c) => c.n));
  const dominant = counts.reduce((acc, c) => (c.n > acc.n ? c : acc), { type: '', n: 0 });
  const thin = counts.reduce((acc, c) => (c.n < acc.n ? c : acc), { type: '', n: Infinity });

  const idToIndex = useMemo(() => new Map(nodes.map((n, i) => [n.id, i])), [nodes]);
  const active = hovered ?? pinned;
  const neighbors = useMemo(() => {
    if (!active) return null;
    const hi = idToIndex.get(active);
    if (hi === undefined) return null;
    const set = new Set<string>([active]);
    for (const e of edges) {
      if (e.a === hi) set.add(nodes[e.b]?.id);
      else if (e.b === hi) set.add(nodes[e.a]?.id);
    }
    return set;
  }, [active, edges, nodes, idToIndex]);

  // Clusters: centroid + radius of each present type, from the live node
  // positions. Recomputed every render (incl. each settling frame) since node
  // positions mutate in place behind a stable ref. The halo and the heading are
  // both pinned to this so they track the actual blob instead of a fixed anchor.
  const clusterAcc = new Map<string, { x: number; y: number; n: number }>();
  for (const n of nodes) {
    const cur = clusterAcc.get(n.type) ?? { x: 0, y: 0, n: 0 };
    cur.x += n.x;
    cur.y += n.y;
    cur.n += 1;
    clusterAcc.set(n.type, cur);
  }
  const clusters = [...clusterAcc.entries()].map(([type, v]) => {
    const cx = v.x / v.n;
    const cy = v.y / v.n;
    // radius = furthest node from the centroid, so the halo actually contains the blob
    let far = 0;
    for (const n of nodes) {
      if (n.type !== type) continue;
      const d = Math.hypot(n.x - cx, n.y - cy);
      if (d > far) far = d;
    }
    return { type, cx, cy, r: Math.max(46, far + 22) };
  });

  const detail = (pinned ?? hovered) ? nodes[idToIndex.get(pinned ?? hovered ?? '') ?? -1] : undefined;

  if (nodes.length === 0) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-950 py-16 text-center">
        <Boxes className="mx-auto h-10 w-10 text-slate-600" />
        <p className="mt-2 text-sm text-slate-400">No atoms to navigate with the current filter.</p>
      </div>
    );
  }

  const dimNode = (n: SimNode) => {
    if (spotlight) return n.type !== spotlight;
    if (neighbors) return !neighbors.has(n.id);
    return false;
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      {/* The navigator */}
      <div
        className="relative flex-1 select-none overflow-hidden rounded-xl border border-slate-800 shadow-[inset_0_0_80px_rgba(0,0,0,0.6)]"
        style={{ background: 'radial-gradient(circle at 50% 42%, #0b1220 0%, #060a12 55%, #04060b 100%)' }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full touch-none"
          style={{ cursor: panRef.current ? 'grabbing' : 'grab' }}
          role="img"
          aria-label="Holographic atom navigator"
          onWheel={onWheel}
          onPointerDown={onBgPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <defs>
            <pattern id="hexgrid" width="46" height="46" patternUnits="userSpaceOnUse">
              <path d="M46 0H0V46" fill="none" stroke="#1e293b" strokeWidth="0.5" strokeOpacity="0.5" />
            </pattern>
            <radialGradient id="vignette" cx="50%" cy="42%" r="70%">
              <stop offset="60%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
            </radialGradient>
          </defs>

          {/* Static HUD backdrop (not affected by pan/zoom) */}
          <rect x={0} y={0} width={W} height={H} fill="url(#hexgrid)" />

          {/* Camera group: pan + zoom */}
          <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
            {/* type cluster halos — pinned to the live cluster centroid + radius */}
            {clusters.map((c) => (
              <circle
                key={`halo-${c.type}`}
                cx={c.cx}
                cy={c.cy}
                r={c.r}
                fill={colorFor(c.type)}
                opacity={spotlight && spotlight !== c.type ? 0.02 : 0.06}
              />
            ))}

            {/* edges */}
            <g strokeLinecap="round">
              {edges.map((e, i) => {
                const na = nodes[e.a];
                const nb = nodes[e.b];
                if (!na || !nb) return null;
                const on = neighbors ? neighbors.has(na.id) && neighbors.has(nb.id) : true;
                const spot = !spotlight || (na.type === spotlight && nb.type === spotlight);
                const lit = on && spot;
                return (
                  <line
                    key={i}
                    x1={na.x}
                    y1={na.y}
                    x2={nb.x}
                    y2={nb.y}
                    stroke={lit ? colorFor(na.type) : '#334155'}
                    strokeWidth={lit ? 1.4 : 0.6}
                    strokeOpacity={lit ? (neighbors ? 0.85 : 0.35) : 0.08}
                  />
                );
              })}
            </g>

            {/* cluster headings — sit just above each cluster's halo so they stay attached */}
            {clusters.map((c) => (
              <text
                key={`label-${c.type}`}
                x={c.cx}
                y={c.cy - c.r - 8}
                textAnchor="middle"
                fill={colorFor(c.type)}
                fontSize={12}
                letterSpacing={2}
                opacity={spotlight && spotlight !== c.type ? 0.2 : 0.65}
                style={{ pointerEvents: 'none', textTransform: 'uppercase', fontWeight: 600 }}
              >
                {c.type}
              </text>
            ))}

            {/* nodes: soft halo + core + optional pin ring */}
            <g>
              {nodes.map((n) => {
                const dim = dimNode(n);
                const isActive = n.id === active;
                const isPinned = n.id === pinned;
                const c = colorFor(n.type);
                const rr = isActive ? n.r + 2.5 : n.r;
                const showLabel = !dim && (view.k >= LABEL_ALL_ZOOM || isActive || (neighbors ? neighbors.has(n.id) : false));
                const fs = 11 / view.k; // keep labels a constant screen size regardless of zoom
                const off = (rr + 5) / view.k;
                const label = showLabel ? labelFor(n.atom) : '';
                return (
                  <g
                    key={n.id}
                    className="cursor-pointer"
                    opacity={dim ? 0.22 : 1}
                    onPointerDown={(e) => onNodePointerDown(e, n.id)}
                    onPointerEnter={() => setHovered(n.id)}
                    onPointerLeave={() => setHovered((cur) => (cur === n.id ? null : cur))}
                  >
                    <circle cx={n.x} cy={n.y} r={rr * 2.6} fill={c} opacity={isActive ? 0.28 : 0.14} />
                    <circle
                      cx={n.x}
                      cy={n.y}
                      r={rr}
                      fill={c}
                      fillOpacity={0.55 + n.conf * 0.45}
                      stroke={isActive ? '#e2e8f0' : c}
                      strokeWidth={isActive ? 1.6 : 0.8}
                      strokeOpacity={0.9}
                    />
                    {isPinned && (
                      <circle cx={n.x} cy={n.y} r={rr + 5} fill="none" stroke="#e2e8f0" strokeWidth={1} strokeOpacity={0.8} />
                    )}
                    {label && (
                      <text
                        x={n.x + off}
                        y={n.y + fs * 0.35}
                        fontSize={fs}
                        fill={isActive ? '#f1f5f9' : '#cbd5e1'}
                        stroke="#04060b"
                        strokeWidth={fs * 0.28}
                        paintOrder="stroke"
                        strokeLinejoin="round"
                        style={{ pointerEvents: 'none', fontWeight: isActive ? 600 : 400 }}
                      >
                        {label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </g>

          <rect x={0} y={0} width={W} height={H} fill="url(#vignette)" style={{ pointerEvents: 'none' }} />
        </svg>

        {/* HUD chrome */}
        <div
          className={
            'pointer-events-none absolute left-3 top-3 flex items-center gap-2 font-mono ' +
            'text-[10px] uppercase tracking-widest text-cyan-300/70'
          }
        >
          <Waypoints className="h-3.5 w-3.5" />
          {nodes.length} nodes · {edges.length} links
          {alphaRef.current > ALPHA_MIN && <span className="text-cyan-400/60">· settling</span>}
        </div>

        <div className="absolute bottom-3 left-3 font-mono text-[10px] text-slate-500">
          drag field to pan · scroll to zoom · drag a node · click to pin
          {capped > 0 && (
            <span className="text-amber-400/70">
              {' '}
              · showing first {MAX_NODES} of {total}
            </span>
          )}
        </div>

        <div className="absolute right-3 top-3 flex flex-col gap-1.5">
          <button type="button" onClick={() => zoomBy(1.2)} className={HUD_BTN} aria-label="Zoom in">
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => zoomBy(1 / 1.2)} className={HUD_BTN} aria-label="Zoom out">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={resetView} className={HUD_BTN} aria-label="Reset view">
            <Crosshair className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={replay} className={HUD_BTN} aria-label="Replay layout">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Balance readout + inspector */}
      <div className="w-full shrink-0 space-y-4 lg:w-64">
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-3">
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            Balance by type · {total} atoms
          </p>
          <ul className="space-y-1.5">
            {counts.map((c) => {
              const on = !spotlight || spotlight === c.type;
              return (
                <li key={c.type}>
                  <button
                    type="button"
                    onClick={() => setSpotlight((s) => (s === c.type ? null : c.type))}
                    className="w-full text-left"
                    style={{ opacity: on ? 1 : 0.4 }}
                  >
                    <div className="mb-0.5 flex items-center justify-between text-[11px]">
                      <span className="flex items-center gap-1.5 capitalize text-slate-200">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: colorFor(c.type), boxShadow: `0 0 8px ${colorFor(c.type)}` }}
                        />
                        {c.type}
                      </span>
                      <span className="tabular-nums text-slate-400">
                        {c.n} · {Math.round((c.n / total) * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(c.n / maxCount) * 100}%`,
                          background: colorFor(c.type),
                          boxShadow: `0 0 6px ${colorFor(c.type)}`,
                        }}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {counts.length > 1 && dominant.type && (
            <p className="mt-2 text-[10px] leading-snug text-slate-500">
              Most: <span className="capitalize text-slate-300">{dominant.type}</span> · Thinnest:{' '}
              <span className="capitalize text-slate-300">{thin.type}</span>. {edges.length} entity links.
              {spotlight && <span className="text-cyan-400/80"> · spotlight: {spotlight} (click again to clear)</span>}
            </p>
          )}
        </div>

        <div className="min-h-[112px] rounded-xl border border-slate-800 bg-slate-950 p-3 text-xs">
          {detail ? (
            <>
              <div className="mb-1.5 flex items-center gap-2">
                <Badge variant="secondary" className="capitalize">
                  {detail.type}
                </Badge>
                <span className="font-mono text-[10px] text-slate-400">{Math.round(detail.conf * 100)}% conf</span>
                {pinned === detail.id && (
                  <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-cyan-400/70">pinned</span>
                )}
              </div>
              <p className="line-clamp-5 leading-snug text-slate-200">{detail.atom.statement}</p>
              {detail.atom.review_process?.confidence_reason && (
                <p className="mt-1.5 border-l-2 border-slate-700 pl-2 text-[10px] italic leading-snug text-slate-400">
                  {detail.atom.review_process.confidence_reason}
                </p>
              )}
              {(detail.atom.entities?.length ?? 0) > 0 && (
                <p className="mt-1.5 text-[10px] text-slate-500">{detail.atom.entities.map((e) => e.name).join(' · ')}</p>
              )}
              {detail.atom.media_anchor?.[0]?.source_title && (
                <p className="mt-1 truncate font-mono text-[10px] text-slate-600">{detail.atom.media_anchor[0].source_title}</p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-slate-500">
              Hover a node to inspect it, or click to pin. Nodes cluster by type; lines connect atoms that share an entity.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
